/**
 * Onboarding API
 *
 * API calls for onboarding wizard (collections, wizard completion)
 */

import apiClient, { ApiResponse } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

/**
 * Collection summary (list view)
 */
export interface CollectionSummary {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  products: string[];
  colors: string[];
  style?: string;
}

/**
 * Collection detail (full view)
 */
export interface CollectionDetail {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  defaultPrice: number;
  products: CollectionProduct[];
  theme: CollectionTheme;
  style?: string;
}

export interface CollectionProduct {
  slug: string;
  name: string;
  enabledColors: string[];
  imageUrl?: string;
}

export interface CollectionTheme {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

/**
 * Collections list response
 */
export interface CollectionsListResponse {
  data: CollectionSummary[];
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Get all available collections (summary view)
 */
export async function getCollections(): Promise<CollectionSummary[]> {
  const response = await apiClient.get<ApiResponse<CollectionsListResponse>>(
    '/onboarding/collections'
  );
  return response.data.data.data;
}

/**
 * Get collection detail by ID/slug
 */
export async function getCollectionDetail(
  collectionId: string
): Promise<CollectionDetail> {
  const response = await apiClient.get<ApiResponse<CollectionDetail>>(
    `/onboarding/collections/${collectionId}`
  );
  return response.data.data;
}

// ═══════════════════════════════════════════════════════════════════
// PLACEMENT PRESETS
// ═══════════════════════════════════════════════════════════════════

export interface PlacementPresetSummary {
  positionSlug: string;
  presetSlug: string;
  label: string;
  description?: string;
  isDefault: boolean;
  sortOrder: number;
}

export interface CollectionPlacementsResponse {
  collectionSlug: string;
  presets: PlacementPresetSummary[];
}

/**
 * Get placement presets for a collection, grouped by position (front, back)
 */
export async function getCollectionPlacements(
  collectionId: string
): Promise<CollectionPlacementsResponse> {
  const response = await apiClient.get<
    ApiResponse<CollectionPlacementsResponse>
  >(`/onboarding/collections/${collectionId}/placements`);
  return response.data.data;
}

// ═══════════════════════════════════════════════════════════════════
// WIZARD COMPLETION
// ═══════════════════════════════════════════════════════════════════

/**
 * Wizard completion payload
 */
export interface WizardCompletePayload {
  customerEmail: string;
  customerName: string;
  notes?: string;
  shopName: string;
  language: string;
  subdomain: string;
  collectionId: string;
  frontDesign?: { assetId: string; placement: string };
  backDesign?: { assetId: string; placement: string };
}

/**
 * Wizard completion response — matches WizardCompleteResponseDto from backend
 */
export interface WizardCompleteResponse {
  success: boolean;
  sellerId?: string;
  status?: string;
  message?: string;
  urls?: {
    vendor_panel_url?: string;
    activation_url?: string;
    storefront_url?: string;
    shop_url?: string;
  };
  nextSteps?: Array<{ id: string; url: string }>;
  summary?: {
    total: number;
    created: number;
    skipped: number;
    failed: number;
  };
  error?: string;
}

/**
 * Complete the onboarding wizard
 */
export async function completeWizard(
  payload: WizardCompletePayload
): Promise<WizardCompleteResponse> {
  const response = await apiClient.post<ApiResponse<WizardCompleteResponse>>(
    '/onboarding/complete',
    payload,
    { timeout: 120_000 } // Mockup rendering + external API can take >30s
  );
  return response.data.data;
}

// ═══════════════════════════════════════════════════════════════════
// PROJECT STATUS (for polling)
// ═══════════════════════════════════════════════════════════════════

/**
 * Project status response
 */
export interface ProjectStatus {
  project: {
    id: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  };
  mockups: {
    total: number;
    completed: number;
    failed: number;
  };
  external: {
    sellerId?: string;
    onboardedAt?: string;
  };
}

/**
 * Get project status (for polling during mockup generation)
 */
export async function getProjectStatus(
  projectId: string
): Promise<ProjectStatus> {
  const response = await apiClient.get<ApiResponse<ProjectStatus>>(
    `/onboarding/projects/${projectId}/status`
  );
  return response.data.data;
}
